import api from "@/api";

interface ProjectLike {
	Customers?: Array<{ id?: string }> | null;
}

interface ResolveProjectPrefillArgs {
	customerId?: string | null;
	projectId?: string | null;
	project?: ProjectLike | null;
}

interface ResolveProjectPrefillResult {
	// The authoritative project the caller should adopt as `newSelectedProject`. When a fresh fetch
	// succeeds this is the freshly-fetched object; otherwise it is the project that was passed in (or
	// null). Adopting it keeps the dialog's save path — which reads `newSelectedProject.Customers` to
	// decide whether to re-add the contact to the project — consistent with the prefilled contact, so
	// a stale snapshot can't cause the save to re-add a contact that was already removed.
	project: ProjectLike | null;
	customerId: string | undefined;
}

/**
 * LSP-16205: resolve BOTH the authoritative project and the contact id used to prefill the Action*
 * dialogs when launched from a project.
 *
 * Precedence:
 *   1. an explicit `customerId` always wins (no fetch); the passed project is returned unchanged so
 *      the explicit-customerId path keeps its current no-fetch behavior;
 *   2. otherwise, when a `projectId` is known, fetch the project FRESH once and use it plus its first
 *      contact — the project FAB passes a `:project` OBJECT prop whose `Customers` is a stale snapshot,
 *      so the fresh fetch is what catches a contact that was removed/replaced after the snapshot was
 *      taken. The caller adopts the returned `project` so its save path reads the SAME fresh
 *      `Customers` the prefill came from (preventing a stale-snapshot save from re-adding a removed
 *      contact);
 *   3. otherwise fall back to the (possibly stale) `project` prop and its first contact.
 *
 * A failed fresh fetch degrades to the stale snapshot rather than throwing, so a transient network
 * failure never aborts the caller's mounted() hook.
 */
export async function resolveProjectPrefill({
	customerId,
	projectId,
	project
}: ResolveProjectPrefillArgs): Promise<ResolveProjectPrefillResult> {
	if (customerId) {
		return { project: project ?? null, customerId };
	}

	if (projectId) {
		try {
			// Derive the prefill customerId from the SAME project we adopt — never cross-fall-back
			// to the stale snapshot's contact. Otherwise a fresh fetch that returns an EMPTY
			// Customers list would prefill the stale snapshot's removed contact while the dialog
			// adopts the fresh (contact-less) project, and the save path would re-add that removed
			// contact (the exact re-add this helper exists to prevent).
			const resolvedProject = (await api.getProject(projectId)) ?? project ?? null;
			return {
				project: resolvedProject,
				customerId: resolvedProject?.Customers?.[0]?.id ?? undefined
			};
		} catch (error) {
			console.warn(
				`[LSP-16205] fresh project fetch failed for project ${projectId}; falling back to the passed project snapshot`,
				error
			);
		}
	}

	return { project: project ?? null, customerId: project?.Customers?.[0]?.id ?? undefined };
}
